import * as types from './catalogUpdateReferences.types';
import {
  IColumnsSelectOrderCatalogUpdateState,
  IInitialCatalogUpdateReferencesState,
  ISearchOnCatalogSearchTypes,
  ISearchOnCatalogTypes,
  IsetSelectedValuesProps,
} from 'interfaces/catalogUpdateReferences.interface';
import { Dispatch } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import {
  IgetCatalogReferencesAPIAPIResponseData,
  IgetCatalogReferencesAPIAPIResponseDataItem,
  IgetCatalogReferencesAPIBody,
  IupdateCatalogReferencesAPIBody,
} from 'api/endpoints/catalogUpdateReferences/catalogUpdateReferences.response.interface';
import {
  getCatalogUpdateReferencesAPI,
  getCatalogUpdateReferencesFiltersAPI,
  updateCatalogReferencesAPI,
} from 'api/endpoints/catalogUpdateReferences/catalogUpdateReferences.api';
import { notificationErr } from 'views/components/UI/notification';
import { catalogUpdateReferencesError } from 'utils/errorCodes';
import { ITranslate } from 'interfaces/general.interface';

export async function getCatalogItemsAction({
  dispatch,
  catalogBodyFetch,
  brandId,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  catalogBodyFetch: IgetCatalogReferencesAPIBody;
  brandId: string;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  catalogBodyFetch.filter.brand_id.value = brandId;
  const getCatalog = await getCatalogData({
    dispatch,
    catalogBodyFetch,
    signal,
    token,
  });
  if (!getCatalog) {
    notificationErr(catalogUpdateReferencesError.getCatalogItemsAction_001, translate);
    return false;
  }
  return true;
}

export async function getFiltersAction({
  dispatch,
  brandId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  brandId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_FILTERS, payload: true });
    const response = await getCatalogUpdateReferencesFiltersAPI({
      brandId,
      token,
    });

    if (response.response.status === 200) {
      dispatch({ type: types.SET_FILTERS, payload: response.data });
      return true;
    }
    notificationErr(catalogUpdateReferencesError.getFiltersAction_002, translate);
    return false;
  } catch (err) {
    notificationErr(catalogUpdateReferencesError.getFiltersAction_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_FILTERS, payload: false });
  }
}

export async function searchOnCatalogItemsAction({
  dispatch,
  catalogBodyFetch,
  searchField,
  searchText,
  searchType,
  type,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  catalogBodyFetch: IgetCatalogReferencesAPIBody;
  searchField: string;
  searchText: string | string[];
  searchType: ISearchOnCatalogSearchTypes;
  type: ISearchOnCatalogTypes;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  if (type === 'add') {
    if (searchType === '%ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: `%${searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchType === '%ILIKE') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: `%${searchText}`,
          operator: 'ILIKE',
        },
      };
    } else if (searchType === 'ILIKE%') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: `${searchText}%`,
          operator: 'ILIKE',
        },
      };
    } else if (searchType === 'IS NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: null,
          operator: 'IS NULL',
        },
      };
    } else if (searchType === 'IS NOT NULL') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: null,
          operator: 'IS NOT NULL',
        },
      };
    } else if (searchType === 'IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: searchText,
          operator: 'IN',
        },
      };
    } else if (searchType === 'NOT IN') {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: searchText,
          operator: 'NOT IN',
        },
      };
    } else {
      catalogBodyFetch.filter = {
        ...catalogBodyFetch.filter,
        [searchField]: {
          value: searchText,
          operator: searchType,
        },
      };
    }
  } else {
    delete catalogBodyFetch.filter[searchField];
  }
  const getCatalog = await getCatalogData({
    dispatch,
    catalogBodyFetch,
    signal,
    token,
  });

  if (!getCatalog) {
    notificationErr(
      catalogUpdateReferencesError.searchOnCatalogItemsAction_001,
      translate
    );
    return false;
  }
  return true;
}

export async function updateReferencesAction({
  dispatch,
  column,
  value,
  catalogBodyFetch,
  selectedValues,
  signal,
  token,
  translate,
}: {
  dispatch: React.Dispatch<any>;
  column: string;
  value: string | string[];
  catalogBodyFetch: IgetCatalogReferencesAPIBody;
  selectedValues: Array<IsetSelectedValuesProps>;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_REFERENCES, payload: true });
    const getColumn = () => {
      if (column === 'color') {
        return 'color_id';
      } else if (column === 'season_id') {
        return 'seasons';
      } else {
        return column;
      }
    };
    for (const item of selectedValues) {
      delete item.season_id;
      if (!item.sku_id) {
        delete item.sku_id;
      }
    }
    const body: IupdateCatalogReferencesAPIBody = {
      update: {
        [getColumn()]: value,
      },
      filter: selectedValues,
    };
    const response = await updateCatalogReferencesAPI(body, token);

    if (response.response.status === 200) {
      dispatch({ type: types.SET_SELECTED_VALUES, payload: [] });
      return await getCatalogData({
        dispatch,
        catalogBodyFetch,
        signal,
        token,
      });
    }
    notificationErr(catalogUpdateReferencesError.updateReferencesAction_002, translate);
    return false;
  } catch (err) {
    notificationErr(catalogUpdateReferencesError.updateReferencesAction_001, translate);
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_UPDATE_REFERENCES, payload: false });
  }
}

// COLUMNS ORDER /////////////////////////////////
export function setColumnsOrderAction({
  dispatch,
  value,
}: {
  dispatch: Dispatch<any>;
  value: Record<string, IColumnsSelectOrderCatalogUpdateState>;
}) {
  dispatch({ type: types.SET_COLUMNS_ORDER, payload: value });
}

export function moveColumnsOrderAction({
  dispatch,
  result,
  columnsOrder,
}: {
  dispatch: Dispatch<any>;
  result: DropResult;
  columnsOrder: Record<string, IColumnsSelectOrderCatalogUpdateState>;
}) {
  if (!result.destination) return;
  const items = Object.entries(columnsOrder);
  const [reorderedItem] = items.splice(result.source.index, 1);
  items.splice(result.destination.index, 0, reorderedItem);
  const updatedItems = items.map(([key, column], index) => {
    return [key, { ...column, order: index }];
  });
  dispatch({ type: types.SET_COLUMNS_ORDER, payload: Object.fromEntries(updatedItems) });
}

export function checkColumnAction({
  dispatch,
  columnKey,
  columnsOrder,
}: {
  dispatch: Dispatch<any>;
  columnKey: string;
  columnsOrder: Record<string, IColumnsSelectOrderCatalogUpdateState>;
}) {
  dispatch({
    type: types.SET_COLUMNS_ORDER,
    payload: {
      ...columnsOrder,
      [columnKey]: {
        ...columnsOrder[columnKey],
        show: !columnsOrder[columnKey].show,
      },
    },
  });
}

export async function handlePaginationAction({
  dispatch,
  page,
  pageSize,
  catalogUpdateReferencesState,
  signal,
  token,
  translate,
}: {
  dispatch: Dispatch<any>;
  page: number;
  pageSize: number;
  catalogUpdateReferencesState: IInitialCatalogUpdateReferencesState;
  signal: AbortSignal;
  token: string;
  translate: ITranslate;
}) {
  catalogUpdateReferencesState.catalogBodyFetch.index =
    page === 1 ? 0 : page * pageSize - pageSize;
  catalogUpdateReferencesState.catalogBodyFetch.limit = pageSize;
  const getCatalog = await getCatalogData({
    dispatch,
    catalogBodyFetch: catalogUpdateReferencesState.catalogBodyFetch,
    signal,
    token,
  });
  if (!getCatalog) {
    notificationErr(catalogUpdateReferencesError.handlePaginationAction_001, translate);
    return false;
  }
  return true;
}

/////////////////////////////////////////////////////
/////////////////////////////////////////////////////
export async function getCatalogData({
  dispatch,
  catalogBodyFetch,
  signal,
  token,
}: {
  dispatch: React.Dispatch<any>;
  catalogBodyFetch: IgetCatalogReferencesAPIBody;
  signal: AbortSignal;
  token: string;
}): Promise<boolean> {
  let values: IgetCatalogReferencesAPIAPIResponseData = {
    count: 0,
    total: 0,
    data: [] as Array<IgetCatalogReferencesAPIAPIResponseDataItem>,
  };
  try {
    dispatch({ type: types.SET_IS_LOADING_CATALOG, payload: true });
    const catalogFetch = await getCatalogUpdateReferencesAPI(
      catalogBodyFetch,
      signal,
      token
    );

    if (catalogFetch.response.status === 200) {
      for (const item of catalogFetch.data.data) {
        const transformItem: IgetCatalogReferencesAPIAPIResponseDataItem = {
          ...item,
          key: `${item.product_id}-${item.reference_id}-${item.season_id}-${item.sku_id ?? ''}`,
        };
        values.data.push(transformItem);
      }
      values.count = catalogFetch.data.count;
      values.total = catalogFetch.data.total;
      dispatch({ type: types.SET_CATALOG_ITEMS, payload: values ?? [] });
      return true;
    }
    return false;
  } catch (err) {
    return false;
  } finally {
    dispatch({ type: types.SET_IS_LOADING_CATALOG, payload: false });
  }
}
